import React, { useEffect, useState } from 'react';
import Box from '@mui/material/Box';
import styles from 'components/medicalHistory/page.module.scss';
import Stack from '@mui/material/Stack';
import Button from 'components/common/button';
import SearchableTextBox from 'components/common/searchableTextBox';
import { filterIntakeFormSettings, isEmptyString } from 'utils/helper';
import { useSelector } from 'react-redux';
import { RootState } from 'store/store';
import {
  StateTypeLocalStorage,
  usePersistMHReducer,
} from 'reducer/medicalHistory';
import { OptionTypeOfSurgicalHistory } from 'components/medicalHistory/types/types';
import MetaTag from 'components/meta';
import dayjs from 'dayjs';
import { NullablePatientDTO } from 'types/patients';

type Props = {
  nextPage: number;
  currentPage: number;
  actionPage: string;
  nextOrSubmitButton: (nextPage: number, submit?: boolean) => void;
};

function SurgicalHistoryScreen({
  nextPage,
  nextOrSubmitButton,
  actionPage,
}: Props) {
  const [surgicalHistory, setSurgicalHistory] = useState<
    OptionTypeOfSurgicalHistory[]
  >([]);
  const [state, dispatch] = usePersistMHReducer();
  const [options, setOptions] = useState<OptionTypeOfSurgicalHistory[]>([]);

  const intakeFormSettings = useSelector(
    (state: RootState) => state.intakeFormSettings.value,
  );

  const patient = useSelector(
    (state: RootState) => state.patient,
  ) as NullablePatientDTO;

  useEffect(() => {
    if (intakeFormSettings) {
      const surgicalHistorySettings = filterIntakeFormSettings(
        intakeFormSettings,
        'surgicalHistorySettings',
      );

      const mappingOptions: OptionTypeOfSurgicalHistory[] = surgicalHistorySettings.map(
        (item: { surgeryName: string }) => ({
          value: item.surgeryName,
          label: item.surgeryName,
        }),
      );

      setOptions(mappingOptions);
    }
  }, [intakeFormSettings]);

  const clickNextOrSubmit = async (nextPage: number) => {
    console.log('surgicalHistory', surgicalHistory);
    const invalidDate = surgicalHistory.find(
      (item) => !isEmptyString(item.year) && (item.isError
        || item.year === 'Invalid Date'
        || Number(item.year) > Number(dayjs().format('YYYY'))
        || Number(item.year) < 1900),
    );

    if (invalidDate) {
      return;
    }

    await dispatch({
      type: actionPage,
      name: 'Surgical History',
      value: surgicalHistory,
    });
    nextOrSubmitButton(nextPage);
  };

  const getSurgicalHistoryItem = (items: OptionTypeOfSurgicalHistory[]) => {
    setSurgicalHistory(items);
  };

  useEffect(() => {
    if (state) {
      const surgicalHistory = state.find(
        (p: StateTypeLocalStorage) => p.name === actionPage,
      );
      if (surgicalHistory.value.length) {
        setSurgicalHistory(surgicalHistory.value);
      } else {
        const mappingData = (patient?.surgicalHistories ?? []).map(
          (item) => ({
            value: item?.surgeryName ?? '',
            label: item?.surgeryName ?? '',
            year: item?.surgeryYear ?? null,
            isError: false,
          }),
        );
        setSurgicalHistory(mappingData);
      }
    }
  }, []);

  return (
    <>
      <MetaTag title="Surgical History" />
      <Box pb={3} mb={3}>
        <Box>
          <h2 className={`marginTopZero ${styles.titleText}`}>
            Surgical History
          </h2>
          <p className={styles.titleTextSubText}>
            Choose all that you have had. All surgeries are helpful for your
            doctor to know, even if they do not seem related to today’s visit
          </p>
          <Stack>
            <SearchableTextBox
              option={options}
              id="Surgical-history"
              placeholder="Search for type of surgery"
              onChange={getSurgicalHistoryItem}
              value={surgicalHistory}
              isSurgical
            />
            <p className={styles.separator} />
          </Stack>
        </Box>
        <div className="next-button-fixed">
          <Button
            onClick={() => clickNextOrSubmit(nextPage)}
          >
            Next
          </Button>
        </div>
      </Box>
    </>
  );
}

export default SurgicalHistoryScreen;
